import AIChatMessage from '../models/AIChatMessage';

const CHAT_HISTORY_PREFIX = 'article_chat_history_';
const CHAT_HISTORY_RETENTION_DAYS = 30; // Keep chat history for 30 days

function isHistoryExpired(lastUpdatedIso: string): boolean {
  const lastUpdated = new Date(lastUpdatedIso);
  const now = new Date();
  const daysDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 3600 * 24);
  return daysDiff > CHAT_HISTORY_RETENTION_DAYS;
}

function collectLocalStorageKeysWithPrefix(prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys;
}

function reviveMessageTimestamps(messages: AIChatMessage[]): AIChatMessage[] {
  return messages.map((message) => ({
    ...message,
    timestamp: new Date(message.timestamp),
  }));
}

export interface StoredChatHistory {
  messages: AIChatMessage[];
  lastUpdated: string;
}

export class ChatHistoryStorage {
  private static getStorageKey(articleId: string): string {
    return `${CHAT_HISTORY_PREFIX}${articleId}`;
  }

  static getChatHistory(articleId: string): AIChatMessage[] {
    try {
      const stored = localStorage.getItem(this.getStorageKey(articleId));
      if (!stored) {
        return [];
      }

      const parsedHistory: StoredChatHistory = JSON.parse(stored);

      parsedHistory.messages = reviveMessageTimestamps(parsedHistory.messages);

      if (isHistoryExpired(parsedHistory.lastUpdated)) {
        // History is expired, remove it
        this.clearChatHistory(articleId);
        return [];
      }

      return parsedHistory.messages || [];
    } catch (error) {
      console.warn('Failed to load chat history for article', articleId, error);
      return [];
    }
  }

  static saveChatHistory(articleId: string, messages: AIChatMessage[]): void {
    try {
      // Only save user messages as per requirements
      const userMessages = messages.filter((msg) => msg.role === 'user');

      const historyToStore: StoredChatHistory = {
        messages: userMessages,
        lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(this.getStorageKey(articleId), JSON.stringify(historyToStore));
    } catch (error) {
      console.warn('Failed to save chat history for article', articleId, error);
    }
  }

  static clearChatHistory(articleId: string): void {
    try {
      localStorage.removeItem(this.getStorageKey(articleId));
    } catch (error) {
      console.warn('Failed to clear chat history for article', articleId, error);
    }
  }

  static clearAllChatHistories(): void {
    try {
      collectLocalStorageKeysWithPrefix(CHAT_HISTORY_PREFIX).forEach((key) =>
        localStorage.removeItem(key),
      );
    } catch (error) {
      console.warn('Failed to clear all chat histories', error);
    }
  }

  static cleanupExpiredHistories(): void {
    try {
      const keysToRemove = collectLocalStorageKeysWithPrefix(CHAT_HISTORY_PREFIX).filter((key) => {
        try {
          const stored = localStorage.getItem(key);
          if (!stored) {
            return false;
          }
          const parsedHistory: StoredChatHistory = JSON.parse(stored);
          return isHistoryExpired(parsedHistory.lastUpdated);
        } catch {
          return true;
        }
      });

      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to cleanup expired chat histories', error);
    }
  }
}
